import { useRecorderStore } from "./recorder-session";
import { canClaimRecordings, claimRecording, releaseRecording } from "./recording-claim";
import {
	indexedDbBackend,
	RECORD_EVICT_AFTER_MS,
	type RecordingBackend,
	reassembleChunks,
} from "./recording-store";

/**
 * What became of a recording's destination while the recording was stranded on disk.
 *
 * `unknown` is its own answer rather than a failure, and it is the one that keeps an author's work
 * alive: an app started without a network cannot tell a swept row from an unreachable server, and
 * deleting a tutorial because a request timed out is the worst thing this feature could do.
 */
export type RecoveryVerdict =
	| { kind: "sendable" }
	| { kind: "arrived" }
	| { kind: "unshippable" }
	| { kind: "unknown" };

/** A recording found on disk, described for the author who has to decide about it. */
export interface RecoverableRecording {
	videoId: string;
	label: string;
	href: string;
	durationMs: number;
}

/** A recording worth showing, with everything the send needs already in hand. */
export interface RecoveryOffer {
	recording: RecoverableRecording;
	verdict: RecoveryVerdict;
	chunks: Blob[];
	mimeType: string;
}

/** The moving parts, so the scan can be tested without a browser. */
export interface RecoveryDependencies {
	backend: RecordingBackend;
	canClaim: () => boolean;
	claim: (videoId: string) => Promise<boolean>;
	release: (videoId: string) => void;
	/** Whether this very document is recording, assembling or shipping that recording right now. */
	isLive: (videoId: string) => boolean;
	now: () => number;
}

const LIVE_DEPENDENCIES: RecoveryDependencies = {
	backend: indexedDbBackend,
	canClaim: canClaimRecordings,
	claim: claimRecording,
	release: releaseRecording,
	isLive: (videoId) => useRecorderStore.getState().target?.videoId === videoId,
	now: () => Date.now(),
};

/**
 * Look for a recording this session should be asked about, and clear away the ones it should not.
 *
 * Each record is put through the same order every time, and each step's answer is either "offer",
 * "keep silently" or "delete" — never a mixture. A record kept silently is offered again at the
 * next start; a record deleted is gone, so only the cases where nothing could ever come of it
 * delete.
 *
 * @param owner - `${orgId}:${userId}` of the signed-in session.
 * @param inspect - Asks the server what became of a recording's destination.
 * @param dependencies - Storage, claims and the clock. Defaulted to the real ones.
 * @returns The first record worth offering, or null when there is none.
 */
export async function scanForRecovery(
	owner: string,
	inspect: (videoId: string) => Promise<RecoveryVerdict>,
	dependencies: RecoveryDependencies = LIVE_DEPENDENCIES,
): Promise<RecoveryOffer | null> {
	const headers = await dependencies.backend.listHeaders();
	const oldestFirst = [...headers].sort((a, b) => a.startedAtWallMs - b.startedAtWallMs);

	// Age is decided first and for every identity, because it needs no server, no claim and no
	// organisation — and because a record made under another identity is reachable by nobody else:
	// leaving it to its own session means a browser that never sees it again keeps it for good.
	const current = [];
	for (const header of oldestFirst) {
		const isExpired = dependencies.now() - header.startedAtWallMs > RECORD_EVICT_AFTER_MS;
		if (isExpired && !dependencies.isLive(header.videoId)) {
			await dependencies.backend.remove(header.videoId);
			continue;
		}
		current.push(header);
	}

	// Without Web Locks nothing below can tell a recording that died from one running in another
	// tab, and offering to send a recording still being made is worse than offering nothing.
	// Records are still written and still age out; they are simply never offered.
	if (!dependencies.canClaim()) return null;

	for (const header of current) {
		// Not this session's to see. The video row is scoped to its organisation, so asking the
		// server about it would answer "gone" for a row that is merely invisible — and we would
		// delete a recording over it. It waits, untouched, for the identity that made it.
		if (header.owner !== owner) continue;

		// The recording this very document is making. A lock this document already holds answers
		// "yours" to its own claim below, so the claim cannot tell a live take from an abandoned
		// record — and offering it would put Delete within one click of a running recording.
		if (dependencies.isLive(header.videoId)) continue;

		// Another tab is recording this, or shipping it. Its owner decides its fate.
		if (!(await dependencies.claim(header.videoId))) continue;

		const rows = await dependencies.backend.readChunks(header.videoId);
		// A read that failed is not an empty record. Treating the two alike would throw away a
		// tutorial over a storage error that may well be gone by the next start.
		if (!rows) {
			dependencies.release(header.videoId);
			continue;
		}

		const playable = reassembleChunks(rows);
		if (!playable) {
			dependencies.release(header.videoId);
			await dependencies.backend.remove(header.videoId);
			continue;
		}

		const verdict = await inspect(header.videoId);
		// It arrived after all — the crash came between the last byte and the record being
		// cleared. Nothing to ask, and the page already carries the video.
		if (verdict.kind === "arrived") {
			dependencies.release(header.videoId);
			await dependencies.backend.remove(header.videoId);
			continue;
		}
		if (verdict.kind === "unknown") {
			dependencies.release(header.videoId);
			continue;
		}

		return {
			recording: {
				videoId: header.videoId,
				label: header.label,
				href: header.href,
				durationMs: playable.durationMs,
			},
			verdict,
			chunks: playable.chunks,
			mimeType: header.mimeType,
		};
	}

	return null;
}
